import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FaPlus, FaTrash, FaKey, FaCopy, FaCheck,
  FaToggleOn, FaToggleOff, FaExternalLinkAlt, FaCode
} from 'react-icons/fa';
import { apiKeysApi } from '../../services/api';
import { AdminLayout } from '../../components/admin';

const ApiKeysManagement = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    expires_in_days: '',
    permissions: []
  });
  const [formError, setFormError] = useState('');

  const fetchApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiKeysApi.getAll();
      if (response.success) {
        setApiKeys(response.data.api_keys || []);
      }
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    try {
      const data = {
        name: formData.name,
        permissions: formData.permissions.length > 0 ? formData.permissions : null,
        expires_in_days: formData.expires_in_days ? parseInt(formData.expires_in_days) : null
      };

      const response = await apiKeysApi.create(data);
      if (response.success) {
        setNewKey(response.data.api_key.key);
        setShowModal(false);
        setShowNewKeyModal(true);
        fetchApiKeys();
        resetForm();
      }
    } catch (err) {
      setFormError(err.message || 'An error occurred');
    }
  };

  const handleToggleStatus = async (key) => {
    try {
      const response = await apiKeysApi.update(key.id, { is_active: !key.is_active });
      if (response.success) {
        fetchApiKeys();
      }
    } catch (err) {
      console.error('Failed to toggle status:', err);
      alert(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiKeysApi.delete(id);
      if (response.success) {
        setDeleteConfirm(null);
        fetchApiKeys();
      }
    } catch (err) {
      console.error('Failed to delete API key:', err);
      alert(err.message);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', expires_in_days: '', permissions: [] });
    setFormError('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin + '/api';

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-dark-800">API Keys</h1>
            <p className="text-dark-500 mt-1">Manage API keys for external access to your blogs</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <FaPlus />
            <span>Create API Key</span>
          </button>
        </div>

        {/* API Documentation Card */}
        <motion.div
          className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl p-6 text-white"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <FaCode className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">API Documentation</h3>
              <p className="text-white/80 mb-4">
                Use your API key to access blogs from external applications.
              </p>
              <div className="bg-black/20 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                <p className="text-white/60 mb-2"># List all blogs</p>
                <p className="break-all">curl -H "X-API-Key: YOUR_API_KEY" \</p>
                <p className="break-all pl-4">{apiBaseUrl}/v1/blogs</p>
                <p className="text-white/60 mt-4 mb-2"># Get single blog</p>
                <p className="break-all">curl -H "X-API-Key: YOUR_API_KEY" \</p>
                <p className="break-all pl-4">{apiBaseUrl}/v1/blogs/SLUG</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* API Keys Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="p-12 text-center">
              <FaKey className="w-12 h-12 text-dark-300 mx-auto mb-4" />
              <p className="text-dark-500 mb-4">No API keys created yet</p>
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                <FaPlus />
                <span>Create First API Key</span>
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Name / Key Prefix
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Last Used
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Expires
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {apiKeys.map((key) => (
                    <tr key={key.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-dark-800">{key.name}</p>
                          <p className="text-sm text-dark-400 font-mono">{key.key_prefix}...</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleStatus(key)}
                          className={`flex items-center space-x-2 px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                            key.is_active && !isExpired(key.expires_at)
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          {key.is_active && !isExpired(key.expires_at) ? (
                            <>
                              <FaToggleOn className="w-3 h-3" />
                              <span>Active</span>
                            </>
                          ) : (
                            <>
                              <FaToggleOff className="w-3 h-3" />
                              <span>{isExpired(key.expires_at) ? 'Expired' : 'Inactive'}</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-dark-500">
                        {formatDate(key.last_used_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-dark-500">
                        <span className={isExpired(key.expires_at) ? 'text-red-500' : ''}>
                          {key.expires_at ? formatDate(key.expires_at) : 'Never'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => setDeleteConfirm(key.id)}
                            className="p-2 text-dark-400 hover:text-red-500 transition-colors"
                            title="Revoke"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Endpoint Reference */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-dark-800 mb-4">Available Endpoints</h3>
          <div className="space-y-4">
            <div className="border-b pb-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">GET</span>
                <code className="text-sm text-dark-600">/api/v1/blogs</code>
              </div>
              <p className="text-dark-500 text-sm">List all published blogs with pagination</p>
              <p className="text-dark-400 text-xs mt-1">Query params: page, per_page, search</p>
            </div>
            <div className="border-b pb-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">GET</span>
                <code className="text-sm text-dark-600">/api/v1/blogs/:slug</code>
              </div>
              <p className="text-dark-500 text-sm">Get single blog by slug with full content</p>
            </div>
            <div className="border-b pb-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">GET</span>
                <code className="text-sm text-dark-600">/api/v1/blogs/id/:id</code>
              </div>
              <p className="text-dark-500 text-sm">Get single blog by ID</p>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">GET</span>
                <code className="text-sm text-dark-600">/api/v1/stats</code>
              </div>
              <p className="text-dark-500 text-sm">Get blog statistics (total blogs, views)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-white rounded-xl p-6 max-w-md w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="text-lg font-bold text-dark-800 mb-4">
              Create New API Key
            </h3>

            {formError && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">
                  Key Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Mobile App, External Integration"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">
                  Expiration (Optional)
                </label>
                <select
                  value={formData.expires_in_days}
                  onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Never expires</option>
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="365">1 year</option>
                </select>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-dark-600 hover:text-dark-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Create Key
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* New Key Created Modal */}
      {showNewKeyModal && newKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-white rounded-xl p-6 max-w-lg w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaKey className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-dark-800">API Key Created!</h3>
              <p className="text-dark-500 mt-2">
                Copy this key now. You won't be able to see it again.
              </p>
            </div>

            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <code className="text-sm text-dark-700 break-all flex-1 mr-4">
                  {newKey}
                </code>
                <button
                  onClick={() => copyToClipboard(newKey)}
                  className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                    copiedKey
                      ? 'bg-green-100 text-green-600'
                      : 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                  }`}
                >
                  {copiedKey ? <FaCheck /> : <FaCopy />}
                </button>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 text-sm">
                <strong>Important:</strong> Store this key securely. It provides access to your blog data.
                If compromised, revoke it immediately and create a new one.
              </p>
            </div>

            <button
              onClick={() => { setShowNewKeyModal(false); setNewKey(null); }}
              className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Done
            </button>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-white rounded-xl p-6 max-w-md w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="text-lg font-bold text-dark-800 mb-4">Revoke API Key?</h3>
            <p className="text-dark-500 mb-6">
              Are you sure you want to revoke this API key? Any applications using this key
              will immediately lose access. This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-dark-600 hover:text-dark-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Revoke
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ApiKeysManagement;
